import React, {useState} from 'react';
import {Search, Filter, Download, RefreshCw, X} from 'lucide-react';
import Button from './Button';
import AppInput from './AppInput';
import {FilterConfig} from '../../../types/table.types';

interface TableToolbarProps {
  // البحث
  searchValue?: string;
  onSearchChange?: (searchValue: string) => void;
  searchPlaceholder?: string;

  // الفلترة
  filters?: FilterConfig[];
  onFiltersChange?: (filters: FilterConfig[]) => void;
  filterableColumns?: Array<{
    key: string;
    label: string;
    type: 'text' | 'number' | 'select' | 'date';
    options?: Array<{value: string; label: string}>;
  }>;

  // التحديث
  onRefresh?: () => void;
  refreshing?: boolean;

  // التصدير
  exportable?: boolean;
  onExport?: (format: 'csv' | 'excel' | 'pdf') => void;

  // إضافي
  extraActions?: React.ReactNode;
  className?: string;
}

const TableToolbar: React.FC<TableToolbarProps> = ({
  searchValue = '',
  onSearchChange,
  searchPlaceholder = 'البحث...',
  filters = [],
  onFiltersChange,
  filterableColumns = [],
  onRefresh,
  refreshing = false,
  exportable = false,
  onExport,
  extraActions,
  className = ''
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const [localFilters, setLocalFilters] = useState<FilterConfig[]>(filters);

  const handleSearchChange = (value: string) => {
    onSearchChange?.(value);
  };

  const handleFilterChange = (key: string, value: string | number | boolean | null, operator: FilterConfig['operator'] = 'contains') => {
    const newFilters = localFilters.filter(f => f.key !== key);
    if (value !== null && value !== '') {
      newFilters.push({key, value, operator});
    }
    setLocalFilters(newFilters);
    onFiltersChange?.(newFilters);
  };

  const handleClearFilters = () => {
    setLocalFilters([]);
    onFiltersChange?.([]);
  };

  const handleRefresh = () => {
    onRefresh?.();
  };

  const handleExport = (format: 'csv' | 'excel' | 'pdf') => {
    onExport?.(format);
  };

  const hasActiveFilters = filters.length > 0;

  return (
    <div className={`bg-white border-b border-gray-200 p-6 ${className}`}>
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
        {/* البحث */}
        <div className="flex-1 min-w-0">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <AppInput
              type="text"
              value={searchValue}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={searchPlaceholder}
              className="pr-10 w-full"
            />
          </div>
        </div>

        {/* الأزرار */}
        <div className="flex items-center gap-3">
          {/* زر الفلترة */}
          {filterableColumns.length > 0 && (
            <Button
              variant={hasActiveFilters ? 'primary' : 'ghost'}
              size="small"
              onClick={() => setShowFilters(!showFilters)}
              icon={<Filter className="w-4 h-4" />}
            >
              فلترة
              {hasActiveFilters && (
                <span className="mr-1 bg-white text-brand-600 rounded-full w-5 h-5 text-xs flex items-center justify-center">
                  {filters.length}
                </span>
              )}
            </Button>
          )}

          {/* زر التحديث */}
          {onRefresh && (
            <Button
              variant="ghost"
              size="small"
              onClick={handleRefresh}
              disabled={refreshing}
              icon={<RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />}
            >
              تحديث
            </Button>
          )}

          {/* زر التصدير */}
          {exportable && onExport && (
            <div className="relative group">
              <Button
                variant="ghost"
                size="small"
                icon={<Download className="w-4 h-4" />}
              >
                تصدير
              </Button>
              <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                <div className="py-1">
                  <button
                    onClick={() => handleExport('csv')}
                    className="block w-full text-right px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    تصدير CSV
                  </button>
                  <button
                    onClick={() => handleExport('excel')}
                    className="block w-full text-right px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    تصدير Excel
                  </button>
                  <button
                    onClick={() => handleExport('pdf')}
                    className="block w-full text-right px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    تصدير PDF
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* إجراءات إضافية */}
          {extraActions}
        </div>
      </div>

      {/* لوحة الفلترة */}
      {showFilters && filterableColumns.length > 0 && (
        <div className="mt-4 p-4 bg-gray-50 rounded-md border">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-gray-900">فلترة البيانات</h4>
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="small"
                onClick={handleClearFilters}
                icon={<X className="w-4 h-4" />}
              >
                مسح الفلاتر
              </Button>
              <Button
                variant="ghost"
                size="small"
                onClick={() => setShowFilters(false)}
              >
                إغلاق
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filterableColumns.map(column => {
              const currentFilter = filters.find(f => f.key === column.key);

              return (
                <div key={column.key} className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {column.label}
                  </label>

                  {column.type === 'select' && column.options ? (
                    <select
                      value={currentFilter?.value?.toString() || ''}
                      onChange={(e) => handleFilterChange(column.key, e.target.value || null, 'equals')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary/30 focus:border-brand-500"
                    >
                      <option value="">الكل</option>
                      {column.options.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  ) : column.type === 'date' ? (
                    <AppInput
                      type="date"
                      value={currentFilter?.value?.toString() || ''}
                      onChange={(e) => handleFilterChange(column.key, e.target.value || null, 'equals')}
                      className="w-full"
                    />
                  ) : (
                    <AppInput
                      type={column.type === 'number' ? 'number' : 'text'}
                      value={currentFilter?.value?.toString() || ''}
                      onChange={(e) => handleFilterChange(column.key, e.target.value || null, 'contains')}
                      placeholder={`فلترة ${column.label}...`}
                      className="w-full"
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default TableToolbar;
